import { css, Button, Link, tokens, formatTime, formatDate, timestampToDateString, timestampToTimeString } from "@balabalabom/ui";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { parseDate } from "../lib/date";
import React from "react";
import { ImagePreviewGallery } from "../components/ImagePreviewGallery";
import { useFullscreenIframe } from "../hooks/useFullscreenIframe";
import { toEmbeddableUrl } from "../lib/embedUrl";
import { eventService } from "../lib/getClient";
import { Event as GrpcEvent, EventAudience } from "../generated/events/v1/event_pb";
import { EventRegistrationSection } from "../components/EventRegistrationSection";
import { EventKindBadge, eventKindPalette } from "../components/EventKindBadge";
import { resolveEventKind, type EventKind } from "../lib/eventKind";

function isKidsAudience(event: GrpcEvent.AsObject): boolean {
  const list = event.audiencesList || [];
  // Empty list = pre-migration / legacy rows (treated as kids).
  if (list.length === 0) return true;
  return list.includes(EventAudience.EVENT_AUDIENCE_KIDS);
}

interface EventData {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  address: string;
  image: string;
  details: string;
  requirements: string;
  targetAge: string;
  maxParticipants: number;
  registrationFee: number;
  facebookLink: string;
  registrationOpen: boolean;
  confirmedCount: number;
  waitlistedCount: number;
  type?: EventKind;
  bookcovers?: string[];
  attachments?: { name: string; url: string }[];
  bookURL?: string | string[];
}

// Helper function to convert gRPC Event.AsObject to component EventData interface
const mapGrpcEventToEventData = (grpcEvent: GrpcEvent.AsObject): EventData => {
  // Prefer datetime (single source of truth); fallback to date/time for legacy rows
  const eventDate = grpcEvent.datetime ? timestampToDateString(grpcEvent.datetime) : grpcEvent.date || "";
  const eventTime = grpcEvent.datetime ? timestampToTimeString(grpcEvent.datetime) : grpcEvent.time || "";
  return {
    id: grpcEvent.id,
    title: grpcEvent.title,
    date: eventDate,
    time: eventTime,
    location: grpcEvent.location,
    address: grpcEvent.address,
    image: grpcEvent.imageUrl,
    details: grpcEvent.details,
    requirements: grpcEvent.requirements,
    targetAge: grpcEvent.targetAge,
    maxParticipants: grpcEvent.maxParticipants,
    registrationFee: grpcEvent.registrationFee,
    facebookLink: grpcEvent.facebookLink,
    registrationOpen: grpcEvent.registrationOpen,
    confirmedCount: grpcEvent.confirmedCount ?? 0,
    waitlistedCount: grpcEvent.waitlistedCount ?? 0,
    type: resolveEventKind({
      title: grpcEvent.title,
      details: grpcEvent.details,
    }),
    // These fields may not be in gRPC response, so they're optional
    bookcovers: undefined,
    attachments: undefined,
    bookURL: undefined,
  };
};

export function Event() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { open: openBookIframe, Modal: BookIframeModal } =
    useFullscreenIframe("Book preview");

  // Function to calculate event status based on date
  const getEventStatus = (eventDate: string): "current" | "future" | "past" => {
    const today = new Date();
    const eventDateObj = parseDate(eventDate);

    // Set time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    eventDateObj.setHours(0, 0, 0, 0);

    const timeDiff = eventDateObj.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return "past";
    } else if (daysDiff <= 7) {
      return "current";
    } else {
      return "future";
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch event from gRPC service
        const grpcEvent = await eventService.getEvent(eventId);

        if (!isKidsAudience(grpcEvent)) {
          setEvent(null);
          setError("This event is not available on the kids site.");
          return;
        }

        // Map gRPC event to component EventData interface
        const mappedEvent = mapGrpcEventToEventData(grpcEvent);

        setEvent(mappedEvent);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);
        console.error("Error fetching event from gRPC:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (!event || loading) return;
    if (window.location.hash !== "#event-registration") return;
    const timer = window.setTimeout(() => {
      document
        .getElementById("event-registration")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [event, loading]);


  // Function to format event details with bullet points and sections
  const formatEventDetails = (text: string) => {
    const sections: React.ReactElement[] = [];

    // Find all section headers (emoji + text + colon)
    // Match: 🌱 What You'll Learn:, 🎵 What to Expect:, 👩‍👧‍👦 Who It's For:, etc.
    const sectionHeaderPattern = /(?:🌱|🎵|👩‍👧‍👦|🚫|💵|⚙️)\s+[^🌱🎵👩‍👧‍👦🚫💵⚙️]+?:/g;
    const matches = Array.from(text.matchAll(sectionHeaderPattern));

    // If no sections found, return as simple paragraph
    if (matches.length === 0) {
      return (
        <p
          style={css({
            fontSize: "0.9rem",
            lineHeight: "1.7",
            "@media (min-width: 768px)": { fontSize: "1.1rem" },
          })}
        >
          {text}
        </p>
      );
    }

    // Extract introduction (before first section)
    const firstMatch = matches[0];
    const introPart = text.substring(0, firstMatch.index).trim();

    // Render introduction if exists
    if (introPart) {
      sections.push(
        <p
          key="intro"
          style={css({
            fontSize: "0.9rem",
            lineHeight: "1.7",
            marginBottom: "1.5rem",
            "@media (min-width: 768px)": {
              fontSize: "1.1rem",
              marginBottom: "2rem",
            },
          })}
        >
          {introPart}
        </p>
      );
    }

    // Process each section
    for (let i = 0; i < matches.length; i++) {
      const headerMatch = matches[i];
      const header = headerMatch[0].trim();
      const contentStart = (headerMatch.index || 0) + headerMatch[0].length;
      const contentEnd =
        i < matches.length - 1
          ? matches[i + 1].index || text.length
          : text.length;
      const content = text.substring(contentStart, contentEnd).trim();

      // Extract bullet points and paragraphs from content
      const bullets: string[] = [];
      const paragraphs: string[] = [];
      let paragraphBuffer: string[] = [];

      const pushParagraph = () => {
        if (paragraphBuffer.length) {
          paragraphs.push(paragraphBuffer.join(" "));
          paragraphBuffer = [];
        }
      };

      const lines = content.split(/\r?\n/);
      for (const rawLine of lines) {
        const trimmedLine = rawLine.trim();
        if (!trimmedLine) {
          pushParagraph();
          continue;
        }

        const bulletMatch = trimmedLine.match(/^[✅•]\s*(.+)$/);
        if (bulletMatch) {
          pushParagraph();
          const text = bulletMatch[1].trim();
          if (text) bullets.push(text);
          continue;
        }

        // Skip lines that are just section emojis repeated
        if (trimmedLine.length <= 2 && trimmedLine.match(/^[🌱🎵👩‍👧‍👦🚫💵⚙️]$/)) {
          continue;
        }

        paragraphBuffer.push(trimmedLine);
      }
      pushParagraph();

      // Render section
      sections.push(
        <div
          key={`section-${i}`}
          style={css({
            marginBottom: "2rem",
            "@media (min-width: 768px)": { marginBottom: "2.5rem" },
          })}
        >
          <h3
            style={css({
              fontSize: "1.25rem",
              fontWeight: "700",
              color: tokens.colors.brand.deep,
              marginBottom: "1rem",
              "@media (min-width: 768px)": {
                fontSize: "1.5rem",
                marginBottom: "1.25rem",
              },
            })}
          >
            {header}
          </h3>

          {bullets.length > 0 && (
            <ul
              style={css({
                listStyle: "none",
                padding: 0,
                margin: "0 0 1rem 0",
              })}
            >
              {bullets.map((item, idx) => (
                <li
                  key={idx}
                  style={css({
                    fontSize: "0.9rem",
                    lineHeight: "1.6",
                    marginBottom: tokens.spacing["3"],
                    paddingLeft: tokens.spacing["6"],
                    position: "relative",
                    "@media (min-width: 768px)": {
                      fontSize: "1.1rem",
                      marginBottom: "0.875rem",
                    },
                  })}
                >
                  <span
                    style={css({
                      position: "absolute",
                      left: 0,
                    })}
                  >
                    ✅
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}

          {paragraphs.map((para, idx) => (
            <p
              key={idx}
              style={css({
                fontSize: "0.9rem",
                lineHeight: "1.7",
                marginBottom: "1rem",
                "@media (min-width: 768px)": {
                  fontSize: "1.1rem",
                  marginBottom: "1.25rem",
                },
              })}
            >
              {para}
            </p>
          ))}
        </div>
      );
    }

    return <>{sections}</>;
  };

  // Function to format requirements with bullet points
  const formatRequirements = (text: string) => {
    // Split by comma to check for emoji-separated items
    const parts = text
      .split(/,\s*/)
      .map((part) => part.trim())
      .filter((part) => part);

    // Count how many parts start with emojis
    const emojiStartingParts = parts.filter((part) =>
      part.match(/^[💻🧃🧠⚙️]/)
    );

    // If we have 2+ emoji-starting parts, treat as a bullet list
    // Otherwise, render as a paragraph
    if (emojiStartingParts.length < 2) {
      return (
        <p
          style={css({
            fontSize: "0.9rem",
            lineHeight: "1.7",
            "@media (min-width: 768px)": { fontSize: "1.1rem" },
          })}
        >
          {text}
        </p>
      );
    }

    // Parse emoji-separated items
    const items: string[] = [];

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      // If starts with emoji, it's a new item
      if (trimmed.match(/^[💻🧃🧠⚙️]/)) {
        items.push(trimmed);
      } else {
        // Continue previous item if exists (for text within parentheses, etc.)
        if (items.length > 0) {
          items[items.length - 1] += ", " + trimmed;
        } else {
          // First item doesn't start with emoji, just add it as single paragraph
          return (
            <p
              style={css({
                fontSize: "0.9rem",
                lineHeight: "1.7",
                "@media (min-width: 768px)": { fontSize: "1.1rem" },
              })}
            >
              {text}
            </p>
          );
        }
      }
    }

    return (
      <ul
        style={css({
          listStyle: "none",
          padding: 0,
          margin: 0,
        })}
      >
        {items.map((item, idx) => {
          const trimmed = item.trim();
          if (!trimmed) return null;

          // Extract emoji and text using regex to properly handle all emoji characters
          const emojiRegex = /^(💻|🧃|🧠|⚙️)\s*/u;
          const match = trimmed.match(emojiRegex);

          if (!match) {
            // No emoji found, use bullet
            return (
              <li
                key={idx}
                style={css({
                  fontSize: "0.9rem",
                  lineHeight: "1.6",
                  marginBottom: tokens.spacing["3"],
                  paddingLeft: tokens.spacing["6"],
                  position: "relative",
                  "@media (min-width: 768px)": {
                    fontSize: "1.1rem",
                    marginBottom: "0.875rem",
                  },
                })}
              >
                <span
                  style={css({
                    position: "absolute",
                    left: 0,
                    display: "inline-block",
                    width: "1.25rem",
                  })}
                >
                  •
                </span>
                <span>{trimmed}</span>
              </li>
            );
          }

          const emoji = match[1];
          // Remove the emoji from the start, and also remove ALL instances of this specific emoji from the text
          // This ensures no duplicate emoji characters remain
          let textContent = trimmed
            .replace(emojiRegex, "") // Remove emoji and whitespace from start
            .replace(
              new RegExp(emoji.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
              ""
            ) // Remove all instances of this emoji
            .replace(/[\u200B-\u200D\uFEFF]/g, "") // Remove zero-width characters
            .trim();

          return (
            <li
              key={idx}
              style={css({
                fontSize: "0.9rem",
                lineHeight: "1.6",
                marginBottom: tokens.spacing["3"],
                paddingLeft: tokens.spacing["6"],
                position: "relative",
                "@media (min-width: 768px)": {
                  fontSize: "1.1rem",
                  marginBottom: "0.875rem",
                },
              })}
            >
              <span
                style={css({
                  position: "absolute",
                  left: 0,
                  display: "inline-block",
                  width: "1.25rem",
                })}
              >
                {emoji}
              </span>
              <span>{textContent}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "current":
        return tokens.colors.status.current;
      case "future":
        return tokens.colors.status.future;
      case "past":
        return tokens.colors.status.past;
      default:
        return tokens.colors.status.default;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "current":
        return "Happening In A Week";
      case "future":
        return "Upcoming";
      case "past":
        return "Past Event";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <main
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          padding: "1rem",
          textAlign: "center",
          "@media (min-width: 768px)": {
            padding: "3rem 1.5rem",
          },
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: tokens.colors.brand.accent,
            marginBottom: "1rem",
            "@media (min-width: 768px)": {
              fontSize: "1.5rem",
            },
          })}
        >
          Loading event details...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          padding: "1rem",
          textAlign: "center",
          "@media (min-width: 768px)": {
            padding: "3rem 1.5rem",
          },
        })}
      >
        <div
          style={css({
            fontSize: "1.25rem",
            color: tokens.colors.semantic.error,
            marginBottom: "1rem",
            "@media (min-width: 768px)": {
              fontSize: "1.5rem",
            },
          })}
        >
          {error || "Event not found"}
        </div>
        <Button variant="primary" onClick={() => navigate("/events")}>
          Back to Events
        </Button>
      </main>
    );
  }

  if (!event) {
    return null;
  }

  const bookcovers = (event.bookcovers ?? []).filter(Boolean);
  const uniqueBookcovers = bookcovers.filter(
    (src, index, list) => list.indexOf(src) === index
  );
  const attachments = (event.attachments ?? []).filter(
    (file): file is { name: string; url: string } =>
      Boolean(file && file.name && file.url)
  );
  const bookUrls = Array.isArray(event.bookURL)
    ? event.bookURL
        .map((url) => (typeof url === "string" ? url.trim() : ""))
        .filter(Boolean)
    : typeof event.bookURL === "string" && event.bookURL.trim()
      ? [event.bookURL.trim()]
      : [];
  const uniqueBookUrls = bookUrls.filter(
    (url, index, list) => list.indexOf(url) === index
  );

  const paletteByType: Record<string, string[]> = {
    storytime: eventKindPalette("storytime"),
    diy: eventKindPalette("diy"),
  };

  const getHighlightColor = (event: EventData) => {
    const kind = resolveEventKind(event);
    const palette = paletteByType[kind];
    if (palette?.length) {
      return palette[0];
    }
    return tokens.colors.surfaces.eventHighlight;
  };

  const getBookTitleFromUrl = (url: string, index: number) => {
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split("/").filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      if (lastSegment) {
        const withoutExt = lastSegment.replace(/\.[^/.]+$/, "");
        const words = decodeURIComponent(withoutExt)
          .replace(/[-_]+/g, " ")
          .trim();
        if (words) {
          return words.replace(/\b\w/g, (char) => char.toUpperCase());
        }
      }
    } catch {
      // ignore parsing errors, fallback below
    }
    return `Book ${index + 1}`;
  };

  const isYoutubeUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.toLowerCase();
      return host.includes("youtube.com") || host.includes("youtu.be");
    } catch {
      return false;
    }
  };

  const highlightColor = getHighlightColor(event);

  let lastNonYoutubeTitle: string | null = null;

  return (
    <>
      <main
        style={css({
          maxWidth: "80rem",
          margin: "0 auto",
          padding: "1rem",
          "@media (min-width: 768px)": {
            padding: "3rem 1.5rem",
          },
        })}
      >
        {/* Back Button */}
        <div
          style={css({
            marginBottom: "1rem",
            "@media (min-width: 768px)": {
              marginBottom: "2rem",
            },
          })}
        >
          <Button
            variant="outline"
            onClick={() => navigate("/events")}
            size="sm"
            style={css({
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              "@media (min-width: 768px)": {
                size: "md",
              },
            })}
          >
            ← Back to Events
          </Button>
        </div>

        {/* Event Header */}
        <div
          className="event-card-mobile"
          style={css({
            backgroundColor: highlightColor,
          })}
        >
          <div className="event-content-mobile">
            {/* Event Image */}
            <div className="event-image-mobile">
              <img
                src={event.image}
                alt={event.title}
                className="image-mobile"
              />
            </div>

            {/* Event Info */}
            <div className="event-info-mobile">
              <div style={css({ marginBottom: "0.75rem" })}>
                <EventKindBadge event={event} />
              </div>
              {/* Status Badge */}
              <div
                style={css({
                  display: "inline-block",
                  backgroundColor: getStatusColor(getEventStatus(event.date)),
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: tokens.borderRadius.full,
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  textAlign: "center",
                  "@media (min-width: 768px)": {
                    fontSize: "0.9rem",
                    textAlign: "left",
                  },
                })}
              >
                {getStatusText(getEventStatus(event.date))}
              </div>

              {/* Title */}
              <h1
                className="event-title-mobile"
                style={css({
                  fontSize: "1.75rem",
                  "@media (min-width: 768px)": { fontSize: "2.5rem" },
                })}
              >
                {event.title}
              </h1>

              {/* Date and Time */}
              <div
                className="event-date-mobile"
                style={css({
                  fontSize: "1rem",
                  "@media (min-width: 768px)": { fontSize: "1.3rem" },
                })}
              >
                📅 {formatDate(event.date)} at {formatTime(event.time)}
              </div>

              {/* Location */}
              <div
                className="event-location-mobile"
                style={css({
                  fontSize: "0.9rem",
                  "@media (min-width: 768px)": { fontSize: "1.1rem" },
                })}
              >
                {(() => {
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    event.address || event.location
                  )}`;
                  return (
                    <>
                      📍{" "}
                      <Link href={mapsUrl} external>
                        {event.location}
                      </Link>
                      <br />
                      <span
                        style={css({
                          fontSize: "0.8rem",
                          opacity: 0.8,
                          "@media (min-width: 768px)": { fontSize: "0.9rem" },
                        })}
                      >
                        {event.address}
                      </span>
                    </>
                  );
                })()}
              </div>

              {/* Target Age */}
              <div
                style={css({
                  fontSize: "0.875rem",
                  color: tokens.colors.brand.deep,
                  marginBottom: tokens.spacing["6"],
                  padding: tokens.spacing["3"],
                  backgroundColor: tokens.colors.overlay.warm,
                  borderRadius: tokens.borderRadius.lg,
                  textAlign: "center",
                  "@media (min-width: 768px)": {
                    fontSize: "1rem",
                    textAlign: "left",
                  },
                })}
              >
                👶 Target Age: {event.targetAge}
              </div>

              {/* Scroll to in-app registration when open + free; Facebook for paid */}
              <div className="button-group-mobile">
                {event.registrationOpen &&
                  event.registrationFee === 0 &&
                  getEventStatus(event.date) !== "past" && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => {
                        document
                          .getElementById("event-registration")
                          ?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="button-full-mobile"
                      style={css({
                        "@media (min-width: 640px)": { width: "auto" },
                      })}
                    >
                      Register Now
                    </Button>
                  )}
                {event.registrationOpen &&
                  event.registrationFee !== 0 &&
                  event.facebookLink?.trim() &&
                  getEventStatus(event.date) !== "past" && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => window.open(event.facebookLink, "_blank")}
                      className="button-full-mobile"
                      style={css({
                        "@media (min-width: 640px)": { width: "auto" },
                      })}
                    >
                      Register Now
                    </Button>
                  )}
              </div>
            </div>
          </div>
        </div>

        <div id="event-registration">
          <EventRegistrationSection
            eventId={event.id}
            registrationOpen={event.registrationOpen}
            registrationFee={event.registrationFee}
            maxParticipants={event.maxParticipants}
            confirmedCount={event.confirmedCount}
            waitlistedCount={event.waitlistedCount}
            facebookLink={event.facebookLink}
            isPast={getEventStatus(event.date) === "past"}
            onRegistrationChanged={async () => {
              try {
                const grpcEvent = await eventService.getEvent(event.id);
                setEvent(mapGrpcEventToEventData(grpcEvent));
              } catch (err) {
                console.error("Error refreshing event after registration:", err);
              }
            }}
          />
        </div>

        {/* Event Details */}
        <div
          className="event-card-mobile"
          style={css({
            backgroundColor: tokens.colors.surfaces.card,
            marginBottom: "1.5rem",
            "@media (min-width: 768px)": { marginBottom: "2rem" },
          })}
        >
          <h2
            className="heading-mobile"
            style={css({
              fontSize: "1.5rem",
              marginBottom: "1.5rem",
              "@media (min-width: 768px)": {
                fontSize: "2rem",
                marginBottom: "2rem",
              },
            })}
          >
            Event Details
          </h2>
          <div
            style={css({
              "@media (min-width: 768px)": {
                padding: "0 0.5rem",
              },
            })}
          >
            {formatEventDetails(event.details)}
          </div>
        </div>

        {/* What to Bring */}
        <div
          className="event-card-mobile"
          style={css({
            backgroundColor: tokens.colors.surfaces.bringSection,
            marginBottom: "1.5rem",
            "@media (min-width: 768px)": { marginBottom: "2rem" },
          })}
        >
          <h2
            className="heading-mobile"
            style={css({
              fontSize: "1.5rem",
              marginBottom: "1.5rem",
              "@media (min-width: 768px)": {
                fontSize: "2rem",
                marginBottom: "2rem",
              },
            })}
          >
            What to Bring
          </h2>
          <div
            style={css({
              "@media (min-width: 768px)": {
                padding: "0 0.5rem",
              },
            })}
          >
            {formatRequirements(event.requirements)}
          </div>
        </div>

        {/* Event Information */}
        <div
          className="event-card-mobile"
          style={css({ backgroundColor: tokens.colors.surfaces.infoSection })}
        >
          <h2
            className="heading-mobile"
            style={css({
              fontSize: "1.5rem",
              "@media (min-width: 768px)": { fontSize: "2rem" },
            })}
          >
            Event Information
          </h2>

          <div className="grid-mobile">
            <div
              style={css({
                padding: "1rem",
                backgroundColor: tokens.colors.surfaces.card,
                borderRadius: tokens.borderRadius.xl,
                border: `1px solid ${tokens.colors.neutral[200]}`,
                textAlign: "center",
                "@media (min-width: 640px)": {
                  textAlign: "left",
                },
              })}
            >
              <h4
                style={css({
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: tokens.colors.brand.deep,
                  marginBottom: "0.5rem",
                  "@media (min-width: 768px)": {
                    fontSize: "1.1rem",
                  },
                })}
              >
                Maximum Participants
              </h4>
              <p
                style={css({
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: tokens.colors.brand.accent,
                  "@media (min-width: 768px)": {
                    fontSize: "1.5rem",
                  },
                })}
              >
                {event.maxParticipants}
              </p>
            </div>

            <div
              style={css({
                padding: "1rem",
                backgroundColor: tokens.colors.surfaces.card,
                borderRadius: tokens.borderRadius.xl,
                border: `1px solid ${tokens.colors.neutral[200]}`,
                textAlign: "center",
                "@media (min-width: 640px)": {
                  textAlign: "left",
                },
              })}
            >
              <h4
                style={css({
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: tokens.colors.brand.deep,
                  marginBottom: "0.5rem",
                  "@media (min-width: 768px)": {
                    fontSize: "1.1rem",
                  },
                })}
              >
                Registration Fee
              </h4>
              <p
                style={css({
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: tokens.colors.brand.accent,
                  "@media (min-width: 768px)": {
                    fontSize: "1.5rem",
                  },
                })}
              >
                {event.registrationFee === 0
                  ? "Free"
                  : `$${event.registrationFee}`}
              </p>
            </div>

            <div
              style={css({
                padding: "1rem",
                backgroundColor: tokens.colors.surfaces.card,
                borderRadius: tokens.borderRadius.xl,
                border: `1px solid ${tokens.colors.neutral[200]}`,
                textAlign: "center",
                "@media (min-width: 640px)": {
                  textAlign: "left",
                },
              })}
            >
              <h4
                style={css({
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: tokens.colors.brand.deep,
                  marginBottom: "0.5rem",
                  "@media (min-width: 768px)": {
                    fontSize: "1.1rem",
                  },
                })}
              >
                Registration Status
              </h4>
              <p
                style={css({
                  fontSize: "1.125rem",
                  fontWeight: "bold",
                  color: event.registrationOpen
                    ? tokens.colors.status.current
                    : tokens.colors.status.closed,
                  "@media (min-width: 768px)": {
                    fontSize: "1.2rem",
                  },
                })}
              >
                {event.registrationOpen ? "Open" : "Closed"}
              </p>
            </div>
          </div>
        </div>

        {/* Downloads */}
        {(uniqueBookcovers.length > 0 ||
          attachments.length > 0 ||
          uniqueBookUrls.length > 0) && (
          <div
            className="event-card-mobile"
            style={css({
              backgroundColor: tokens.colors.surfaces.readingList,
              marginTop: "1.5rem",
              "@media (min-width: 768px)": { marginTop: "2rem" },
            })}
          >
            <h2
              className="heading-mobile"
              style={css({
                fontSize: "1.5rem",
                marginBottom: "1.5rem",
                "@media (min-width: 768px)": { fontSize: "2rem" },
              })}
            >
              Storytime Reading List
            </h2>
            {uniqueBookcovers.length > 0 && (
              <ImagePreviewGallery
                images={uniqueBookcovers}
                label={`${event.title} book cover`}
                previewSize={5}
              />
            )}
            {uniqueBookUrls.length > 0 && (
              <div
                style={css({
                  marginTop: tokens.spacing["4"],
                })}
              >
                <h3
                  style={css({
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: tokens.colors.brand.deep,
                    marginBottom: tokens.spacing["2"],
                  })}
                >
                  Read Online
                </h3>
                <ul
                  style={css({
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "grid",
                    gap: tokens.spacing["3"],
                    "@media (min-width: 640px)": {
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                    },
                  })}
                >
                  {uniqueBookUrls.map((url, index) => {
                    const baseTitle = getBookTitleFromUrl(url, index);
                    const isYoutube = isYoutubeUrl(url);
                    const displayTitle =
                      isYoutube && lastNonYoutubeTitle
                        ? lastNonYoutubeTitle
                        : baseTitle;

                    if (!isYoutube) {
                      lastNonYoutubeTitle = baseTitle;
                    }

                    return (
                      <li
                        key={url}
                        className="read-online-card"
                        style={css({
                          padding: tokens.spacing["4"],
                          borderRadius: tokens.borderRadius.xl,
                          backgroundColor: tokens.colors.surfaces.card,
                          border: `1px solid ${tokens.colors.border.subtle}`,
                          boxShadow: tokens.boxShadow.subtle,
                        })}
                      >
                        <div
                          style={css({
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: tokens.spacing["1"],
                            minWidth: 0,
                          })}
                        >
                          <span
                            style={css({
                              fontWeight: 600,
                              color: tokens.colors.brand.deep,
                              wordBreak: "break-word",
                            })}
                            title={displayTitle}
                          >
                            {displayTitle}
                          </span>
                          <span
                            style={css({
                              fontSize: "0.8rem",
                              color: tokens.colors.neutral[500],
                              wordBreak: "break-all",
                              overflowWrap: "anywhere",
                            })}
                          >
                            {url}
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            openBookIframe(toEmbeddableUrl(url), {
                              title: displayTitle,
                            })
                          }
                          className="button-full-mobile read-online-button"
                          style={css({
                            whiteSpace: "nowrap",
                            borderRadius: tokens.borderRadius.full,
                            paddingInline: tokens.spacing["4"],
                            boxShadow: tokens.boxShadow.base,
                            "&:hover": {
                              boxShadow: tokens.boxShadow.md,
                            },
                          })}
                        >
                          {isYoutube ? "Open YouTube" : "Open Book"}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {attachments.length > 0 && (
              <ul
                style={css({
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  marginTop: tokens.spacing["4"],
                  display: "grid",
                  gap: tokens.spacing["4"],
                  "@media (min-width: 640px)": {
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  },
                })}
              >
                {attachments.map((file) => (
                  <li
                    key={file.url}
                    style={css({
                      padding: tokens.spacing["4"],
                      borderRadius: tokens.borderRadius.xl,
                      backgroundColor: tokens.colors.surfaces.card,
                      border: `1px solid ${tokens.colors.border.subtle}`,
                      boxShadow: tokens.boxShadow.subtle,
                    })}
                  >
                    <div
                      style={css({
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: tokens.spacing["2"],
                        color: tokens.colors.brand.deep,
                        wordBreak: "break-word",
                      })}
                      title={file.name}
                    >
                      {file.name}
                    </div>
                    <Link
                      href={file.url}
                      download
                      variant="contrast"
                      underline="always"
                    >
                      Download
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>
      <BookIframeModal />
    </>
  );
}
